import { Calendar, MapPin, Tag, Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Container from "../components/ui/Container";
import Badge from "../components/ui/Badge";
import { SUCCESSES } from "../data/successes";

export default function Successes() {
    return (
        <div className="relative overflow-hidden min-h-screen">
             {/* Background Decor */}
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute top-0 left-[20%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px]" />
                <div className="absolute bottom-0 right-[10%] h-[30%] w-[30%] rounded-full bg-blue-500/5 blur-[100px]" />
            </div>

            <Container className="relative py-14 sm:py-20 lg:py-24">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 border-b border-slate-100 dark:border-slate-800 pb-12">
                    <div className="max-w-2xl">
                        <Badge className="mb-4 bg-brand-50 text-brand-600 dark:bg-brand-900/20 border-brand-100/50 uppercase tracking-[0.2em] text-[10px]">Echte Praxis-Ergebnisse</Badge>
                        <h1 className="text-4xl sm:text-6xl font-heading font-black tracking-tight text-slate-900 dark:text-white uppercase leading-none">
                            Unsere <span className="text-brand-600">Erfolge</span>
                        </h1>
                        <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
                            Hier teilen unsere Kunden und wir selbst die Erfolge im Revier. Echte Bilder, echte Berichte – von Jägern für Jäger.
                        </p>
                    </div>
                </div>

                {/* Info Box */}
                <div className="bg-brand-50 dark:bg-brand-950/20 border border-brand-100 dark:border-brand-900/50 rounded-3xl p-8 mb-20 flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-1">
                        <h3 className="text-xl font-heading font-black text-brand-900 dark:text-brand-400 mb-2 uppercase">Schick uns deinen Erfolg!</h3>
                        <p className="text-brand-800/70 dark:text-brand-500/70 leading-relaxed text-sm">
                            Hast du auch erfolgreich mit unseren Betonrohrfallen gejagt? Sende uns dein Foto und einen kurzen Bericht via WhatsApp oder E-Mail. Wir veröffentlichen es hier (auf Wunsch anonymisiert).
                        </p>
                    </div>
                    <div className="flex gap-4">
                         <a href="mailto:info@tuscherhelmut-betonrohrfallen.de" className="px-6 py-3 bg-brand-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-brand-700 transition-colors">Per E-Mail</a>
                         <a href="https://wa.me/4915152541312" className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-colors">Per WhatsApp</a>
                    </div>
                </div>

                {/* Erfolgskarten (Grid) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                    {SUCCESSES.map((s) => (
                        <div key={s.id} className="group relative flex flex-col rounded-[2.5rem] border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm transition-all duration-300 hover:shadow-2xl hover:shadow-brand-500/10 overflow-hidden">
                            
                            {/* Bild */}
                            <div className="aspect-[4/3] overflow-hidden">
                                 <img src={s.image} alt={s.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-110" />
                                 <div className="absolute top-6 left-6 flex flex-wrap gap-2">
                                     <span className="bg-white/90 dark:bg-slate-900/90 backdrop-blur px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-tight text-brand-600 border border-brand-100 dark:border-brand-900 shadow-sm flex items-center gap-1.5">
                                         <Tag className="h-3 w-3" /> {s.species}
                                     </span>
                                 </div>
                            </div>

                            {/* Content */}
                            <div className="p-8 flex flex-col flex-1">
                                <div className="flex items-center justify-between mb-6 border-b border-slate-50 dark:border-slate-800 pb-4">
                                     <div className="flex items-center gap-4">
                                         <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                                             <MapPin className="h-3 w-3" /> {s.region}
                                         </div>
                                         <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                                             <Calendar className="h-3 w-3" /> {s.date}
                                         </div>
                                     </div>
                                </div>

                                <div className="relative mb-6">
                                     <Quote className="absolute -left-2 -top-2 h-6 w-6 text-brand-100 dark:text-brand-900/40 -z-1" />
                                     <p className="text-slate-600 dark:text-slate-400 italic text-sm leading-relaxed relative">
                                         "{s.quote}"
                                     </p>
                                </div>

                                <div className="mt-auto pt-6 border-t border-slate-50 dark:border-slate-800 flex items-center gap-2">
                                     <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Lockmittel:</span>
                                     <span className="text-[9px] font-black uppercase tracking-widest text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-100 dark:border-slate-700">
                                         {s.bait}
                                     </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer Teaser */}
                <div className="mt-24 text-center">
                    <h4 className="text-2xl font-heading font-black text-slate-900 dark:text-white uppercase mb-6 italic tracking-tight underline decoration-brand-600 decoration-4 underline-offset-8">Interessiert?</h4>
                    <Link to="/shop" className="inline-flex items-center gap-3 bg-brand-600 text-white px-8 py-5 rounded-[2rem] font-heading font-black uppercase tracking-widest hover:bg-brand-700 transition-all hover:scale-105 shadow-xl shadow-brand-500/20 active:scale-95 group">
                        Zur Betonrohrfalle gehen <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-2" />
                    </Link>
                </div>
            </Container>
        </div>
    );
}